// usePowerSyncStatus — реактивный статус sync-сессии PowerSync 2.
//
// Фазы: initializing | connecting | downloading | ready | uploading | offline | error.
// Процент — только при реальном downloadProgress от SDK; иначе stage + pending count.
//
// `initializing` — интервал «первичного синка ещё не было, но SDK и не
// подключается»: открывается локальная WASM-SQLite, provider ждёт ответа
// auth.me, между чекпоинтами гаснет `downloading`. Раньше он маскировался под
// `ready`, из-за чего баннер прогресса молчал ровно в самый долгий момент
// первого открытия после деплоя. Потребитель обязан различать пользователя и
// гостя: у гостя `connect()` не вызывается вообще и `hasSynced` ложен всегда.
import { useStatus } from "@powersync/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { usePendingSyncCount } from "@/hooks/use-pending-sync-count";
import { captureClientError } from "@/lib/observability";
import { getPostHog, isPostHogEnabled } from "@/lib/observability/posthog-client";
import { isClientNetworkSyncError } from "@/lib/powersync/client-network-error";
import { isPowerSyncAuthSuspended } from "@/lib/powersync/jwt-provider";
import {
	httpStatusFromSyncError,
	syncErrorCodeFrom,
	syncErrorEventTitle,
} from "@/lib/powersync/sync-error-code";

export type PowerSyncPhase =
	| "initializing"
	| "connecting"
	| "downloading"
	| "ready"
	| "uploading"
	| "offline"
	| "error";

export interface PowerSyncDownloadProgress {
	percent: number;
	downloaded: number;
	total: number;
}

export interface PowerSyncStatusView {
	phase: PowerSyncPhase;
	isConnected: boolean;
	isConnecting: boolean;
	hasSynced: boolean;
	lastSyncedAt: Date | null;
	downloadProgress: PowerSyncDownloadProgress | null;
	pendingUploadCount: number;
	errorCode: string | null;
}

const UPLOAD_VISIBLE_AFTER_MS = 2_000;

// Порог, после которого отказ синхронизации считается дефектом и уезжает в Hawk.
//
// Инцидент #16: деплой перезапустил `studyhub-powersync`, сервис закрыл
// открытый HTTP-стрим (`close_reason: "process shutdown"`, 11:50:47.610Z),
// Chrome отдал на чтение тела `TypeError: network error` (11:50:47.704Z) — и
// мы завели issue. Тот же клиент переподключился в 11:50:53.365Z и досинкался
// сам. Разрыв долгоживущего стрима не дефект, а его нормальная жизнь: деплой,
// уснувший ноутбук, смена сети, перезагрузка прокси. С мгновенным репортом
// такой issue появлялся бы на КАЖДЫЙ деплой с каждой открытой вкладки.
//
// Дефект — это невосстановление. SDK ретраит каждые `retryDelayMs` (5 с по
// умолчанию, мы его не переопределяем в `connectPowerSync`), поэтому «ошибка
// всё ещё висит через 30 с» = около пяти неудачных попыток подряд — вот это
// уже стоит человеческого разбора.
//
// Считать «висит» по одному тексту ошибки НЕЛЬЗЯ: `downloadError` переживает
// удачный реконнект (разбор — у `activeSyncError` ниже), и полгруппы
// `powersync.sync_error` в Hawk оказались отчётами о синке, который к моменту
// отправки уже работал. Окно ждёт `activeSyncError`, а он гаснет по
// `connected`.
//
// Окно 30 с не отсекает клиентский обрыв стрима: проба `/version.json` со
// своего origin отвечает 200, `online` остаётся true, а fetch к `powersync.*`
// падает с `Failed to fetch`. Такие коды в Hawk не едут
// (`isClientNetworkSyncError`) — иначе группа копится с каждой уснувшей
// вкладки, как после #16 (58 повторов / 19 людей при живом сервисе).
const SYNC_ERROR_REPORT_AFTER_MS = 30_000;
const appStartedAt = Date.now();
let reportedReady = false;
// Хук монтируется в баннере, дашборде, скелете, деталях — без флага на модуль
// каждый экземпляр завёл бы свой таймер и умножил одно событие на число
// подписчиков (в Hawk два повтора с одним timestamp).
let hawkReportedThisEpisode = false;

function reportSyncMetric(
	event: string,
	props: Record<string, string | number | boolean | null | undefined>,
): void {
	if (!isPostHogEnabled()) return;
	try {
		getPostHog()?.capture(event, props);
	} catch {
		// no-op
	}
}

function useDelayedTrue(value: boolean, delayMs: number): boolean {
	const [delayed, setDelayed] = useState(false);
	useEffect(() => {
		if (!value) {
			setDelayed(false);
			return;
		}
		const id = window.setTimeout(() => setDelayed(true), delayMs);
		return () => window.clearTimeout(id);
	}, [value, delayMs]);
	return value && delayed;
}

export function usePowerSyncStatus(): PowerSyncStatusView {
	const online = useOnlineStatus();
	const status = useStatus();
	// Единственный источник числа неотправленных операций.
	//
	// Здесь был собственный поллер `pendingUploadCount()` каждые 5 секунд, а его
	// результат складывался с `usePendingSyncCount()`. Но очередь PowerSync уже
	// входит в этот счётчик под источником `"powersync"` — его наполняет
	// `SyncQueueProvider` ТЕМ ЖЕ вызовом. Сложение удваивало число: баннер
	// показывал 12 вместо 6. Заодно уходит второй фоновый поллер, который на
	// Capacitor круглосуточно гонял `getCrudBatch(100)` через нативный мост.
	const pendingUploadCountValue = usePendingSyncCount();
	const showUploading = useDelayedTrue(status.uploading, UPLOAD_VISIBLE_AFTER_MS);

	const downloadProgress = useMemo((): PowerSyncDownloadProgress | null => {
		const progress = status.downloadProgress;
		if (!progress || progress.totalOperations <= 0) return null;
		return {
			percent: Math.round(progress.downloadedFraction * 100),
			downloaded: progress.downloadedOperations,
			total: progress.totalOperations,
		};
	}, [status.downloadProgress]);

	// Живое соединение снимает эхо прошлого разрыва.
	//
	// `downloadError` ПЕРЕЖИВАЕТ удачный реконнект, и это свойство SDK: ошибка
	// лежит в JS-части статуса и снимается РОВНО одной инструкцией ядра —
	// `DidCompleteSync` (@powersync/shared-internals,
	// `AbstractStreamingSyncImplementation`: одна установка в catch итерации,
	// одно снятие на этой инструкции). Пока очередной чекпоинт не принёс ни
	// одной применённой операции, инструкция не приходит — и текст прошлого
	// разрыва висит в статусе часами при полностью рабочем синке.
	//
	// Инцидент 2026-09-03: у спавшего ноутбука протух PowerSync-JWT, сервис
	// ответил `PSYNC_S2103` в 11:08:02, ТОТ ЖЕ клиент поднял стрим в 11:08:17
	// (`Sync stream started`, чекпоинт 29180) — а вкладка в 11:08:35 завела
	// issue «не восстановилось за 30 с» и держала фазу `error`. Так набралась
	// половина группы `powersync.sync_error` в Hawk. Признак «синк снова
	// работает» знает только `connected`, поэтому решает он, а не текст ошибки.
	//
	// `uploadError` так не залипает: его снимает следующая удачная отправка
	// очереди, и «подключены, но записи не уезжают» — настоящий отказ.
	const activeSyncError = status.connected
		? status.uploadError
		: (status.downloadError ?? status.uploadError);
	const errorCode = syncErrorCodeFrom(activeSyncError);
	// Статус отдельным числом: он решает, в какую группу Hawk уедет отчёт.
	const errorStatus = httpStatusFromSyncError(activeSyncError);

	const phase = useMemo((): PowerSyncPhase => {
		if (!online) return "offline";
		if (errorCode && !status.connecting) return "error";
		if (status.connecting) return "connecting";
		if (status.downloading) return "downloading";
		// Первичного синка ещё не было — «готово» тут было бы неправдой, даже
		// если прямо сейчас ни одно соединение не в полёте.
		if (!status.hasSynced) return "initializing";
		if (showUploading || pendingUploadCountValue > 0) return "uploading";
		return "ready";
	}, [
		errorCode,
		online,
		pendingUploadCountValue,
		showUploading,
		status.connecting,
		status.downloading,
		status.hasSynced,
	]);

	const view = useMemo(
		(): PowerSyncStatusView => ({
			phase,
			isConnected: status.connected,
			isConnecting: status.connecting,
			hasSynced: status.hasSynced ?? false,
			lastSyncedAt: status.lastSyncedAt ?? null,
			downloadProgress,
			pendingUploadCount: pendingUploadCountValue,
			errorCode,
		}),
		[
			downloadProgress,
			errorCode,
			pendingUploadCountValue,
			phase,
			status.connected,
			status.connecting,
			status.hasSynced,
			status.lastSyncedAt,
		],
	);

	// Снимок эпизода для отложенного отчёта. Через ref, а не через зависимости
	// эффекта: счётчик очереди меняется сам по себе, и от него в deps окно
	// ожидания перезапускалось бы, а отчёт не уехал бы никогда.
	const episodeRef = useRef({
		code: errorCode,
		status: errorStatus,
		pending: pendingUploadCountValue,
		synced: view.hasSynced,
	});
	// Без массива зависимостей намеренно: снимок обновляется после каждого
	// рендера, отдельного триггера у него нет.
	useEffect(() => {
		episodeRef.current = {
			code: errorCode,
			status: errorStatus,
			pending: pendingUploadCountValue,
			synced: view.hasSynced,
		};
	});

	// Метрика — сразу на каждый новый код ошибки. PostHog считает ЧАСТОТУ
	// разрывов (качество сети, цена деплоя для открытых вкладок), и дешёвое
	// событие там никому не стоит времени. Hawk — лента дефектов, где событие
	// равно issue и разбору человеком, поэтому каналы расходятся намеренно.
	const meteredCodeRef = useRef<string | null>(null);
	useEffect(() => {
		if (!errorCode || meteredCodeRef.current === errorCode) return;
		meteredCodeRef.current = errorCode;
		reportSyncMetric("sync_error", {
			sync_error_code: errorCode,
			pending_queue_size: episodeRef.current.pending,
		});
	}, [errorCode]);

	// Эпизод, а не сообщение: между попытками SDK может сменить текст ошибки,
	// но это тот же самый непрекращающийся отказ. Будь код в зависимостях,
	// смена текста перезапускала бы окно, и невосстановление осталось бы
	// незамеченным ровно в самом плохом случае.
	const hasSyncError = errorCode !== null;
	useEffect(() => {
		if (!hasSyncError) hawkReportedThisEpisode = false;
		// Офлайн — не наш дефект: браузер прямо сказал, что сети нет. Окно не
		// заводим вовсе и заводим заново, когда сеть вернётся.
		if (!hasSyncError || !online) return;
		if (hawkReportedThisEpisode) return;
		const timer = window.setTimeout(() => {
			// Синк остановлен намеренно — гость на публичной странице, выход из
			// аккаунта, отказ 401 на выдаче токена. Это состояние «синка быть не
			// должно», а не дефект: чинится входом, а не разбором инженером.
			// Без этой проверки каждый гость, чья вкладка попробовала поднять
			// синк, заводил issue в Hawk (инцидент 2026-08-22).
			if (isPowerSyncAuthSuspended()) return;
			if (hawkReportedThisEpisode) return;
			const episode = episodeRef.current;
			const payload = {
				sync_error_code: episode.code,
				sync_error_status: episode.status,
				pending_queue_size: episode.pending,
				// Первичного синка не было — экраны стоят в скелетонах, это
				// сломанный запуск. Сорванный после первого синка человек может
				// и не заметить: данные уже локально.
				has_synced: episode.synced,
			};
			hawkReportedThisEpisode = true;
			reportSyncMetric("sync_error_sustained", payload);
			// Обрыв fetch/стрима на стороне клиента — не дефект. Частота уже
			// уехала в PostHog строкой выше; Hawk оставим для отказов, которые
			// разбираются в нашем коде (HTTP 5xx, правила синка, JWT).
			if (isClientNetworkSyncError(episode.code)) return;
			captureClientError(new Error(syncErrorEventTitle(episode.status)), {
				...payload,
				unrecovered_for_ms: SYNC_ERROR_REPORT_AFTER_MS,
			});
		}, SYNC_ERROR_REPORT_AFTER_MS);
		return () => window.clearTimeout(timer);
	}, [hasSyncError, online]);

	useEffect(() => {
		if (reportedReady) return;
		if (phase !== "ready" || !view.hasSynced) return;
		reportedReady = true;
		const ms = Date.now() - appStartedAt;
		reportSyncMetric("sync_first_ready", {
			sync_first_ready_ms: ms,
			pending_queue_size: pendingUploadCountValue,
		});
	}, [pendingUploadCountValue, phase, view.hasSynced]);

	return view;
}
